import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '../../../components/hostel/superadmin/SuperAdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const ManageRequests = ({ onNavigate, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterHostel, setFilterHostel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterIssueType, setFilterIssueType] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [assignedTo, setAssignedTo] = useState('');

  useEffect(() => {
    if (userInfo?.userId) {
      fetchRequests();
      fetchHostels();
    }
  }, [userInfo]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/superadmin/maintenance-requests/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setRequests(data.requests || []);
      } else {
        setError(data.message || 'Failed to load maintenance requests');
      }
    } catch (err) {
      console.error('Error fetching maintenance requests:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await fetch(`${API_URL}/superadmin/hostels/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setHostels(data.hostels || []);
      }
    } catch (err) {
      console.error('Error fetching hostels:', err);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleOpenUpdate = (request) => {
    setSelectedRequest(request);
    setNewStatus(request.status);
    setAssignedTo(request.assigned_to || '');
    setShowUpdateModal(true);
  };

  const handleUpdateRequest = async (e) => {
    e.preventDefault();

    if (!newStatus) {
      alert('Please select a status');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/superadmin/maintenance-request/${userInfo.userId}/${selectedRequest.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: newStatus,
            assignedTo: assignedTo
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Maintenance request updated successfully!');
        setShowUpdateModal(false);
        setSelectedRequest(null);
        setNewStatus('');
        setAssignedTo('');
        fetchRequests();
      } else {
        alert(data.message || 'Failed to update maintenance request');
      }
    } catch (err) {
      console.error('Error updating maintenance request:', err);
      alert('Failed to update maintenance request');
    }
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch =
      request.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.request_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.room_number?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesHostel = filterHostel === 'all' || request.hostel_name === filterHostel;
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    const matchesIssueType = filterIssueType === 'all' || request.issue_type === filterIssueType;

    return matchesSearch && matchesHostel && matchesStatus && matchesIssueType;
  });

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-warning';
      case 'in-progress': return 'status-info';
      case 'completed': return 'status-active';
      case 'cancelled': return 'status-danger';
      default: return 'status-default';
    }
  };

  const getIssueTypeIcon = (issueType) => {
    const icons = {
      electrical: '⚡',
      plumbing: '🚰',
      furniture: '🪑',
      carpentry: '🔨',
      appliances: '🔌',
      internet: '🌐',
      hvac: '❄️',
      other: '🔧'
    };
    return icons[issueType] || '🔧';
  };

  return (
    <SuperAdminLayout
      onNavigate={onNavigate}
      userInfo={userInfo}
      activePage="hostel-superadmin-manage-requests"
      pageTitle="All Maintenance Requests 🔧"
    >
      <style>{`
        .stats-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          border: 2px solid rgba(82,171,152,0.15);
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-label {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
        }

        .controls-section {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: 300px;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 14px 20px 14px 50px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .search-icon {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 20px;
          color: #52ab98;
        }

        .filter-select {
          padding: 14px 20px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          color: #2b6777;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
        }

        .filter-select:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .requests-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 24px;
        }

        .request-card {
          background: white;
          border-radius: 20px;
          padding: 28px;
          border: 2px solid rgba(82,171,152,0.15);
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .request-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 6px;
          height: 100%;
          background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
        }

        .request-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.15);
          border-color: rgba(82,171,152,0.3);
        }

        .request-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 20px;
        }

        .request-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          flex-shrink: 0;
        }

        .request-info {
          flex: 1;
        }

        .request-number {
          font-size: 14px;
          font-weight: 800;
          color: #52ab98;
          margin-bottom: 4px;
        }

        .request-student {
          font-size: 18px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .request-date {
          font-size: 12px;
          color: rgba(43,103,119,0.6);
        }

        .request-description {
          font-size: 14px;
          color: rgba(43,103,119,0.8);
          line-height: 1.5;
          margin-bottom: 16px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .request-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .detail-label {
          font-size: 11px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 13px;
          font-weight: 700;
          color: #2b6777;
        }

        .request-actions {
          display: flex;
          gap: 10px;
          padding-top: 20px;
          border-top: 2px solid rgba(82,171,152,0.1);
        }

        .action-btn-view,
        .action-btn-update {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .action-btn-view {
          background: rgba(82,171,152,0.15);
          color: #2b6777;
        }

        .action-btn-view:hover {
          background: rgba(82,171,152,0.25);
          transform: translateY(-2px);
        }

        .action-btn-update {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(43,103,119,0.3);
        }

        .action-btn-update:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(43,103,119,0.4);
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .status-active {
          background: rgba(34,197,94,0.15);
          color: #16a34a;
        }

        .status-warning {
          background: rgba(251,146,60,0.15);
          color: #ea580c;
        }

        .status-info {
          background: rgba(59,130,246,0.15);
          color: #2563eb;
        }

        .status-danger {
          background: rgba(239,68,68,0.15);
          color: #dc2626;
        }

        .status-default {
          background: rgba(107,114,128,0.15);
          color: #4b5563;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.6);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 24px;
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 24px 70px rgba(43,103,119,0.3);
        }

        .modal-header {
          padding: 28px 32px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 900;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(82,171,152,0.15);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: rgba(82,171,152,0.25);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 32px;
        }

        .detail-row {
          display: grid;
          grid-template-columns: 180px 1fr;
          gap: 16px;
          padding: 16px 0;
          border-bottom: 1px solid rgba(82,171,152,0.1);
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-row-label {
          font-size: 13px;
          font-weight: 700;
          color: rgba(43,103,119,0.7);
          text-transform: uppercase;
        }

        .detail-row-value {
          font-size: 15px;
          font-weight: 600;
          color: #2b6777;
          word-break: break-word;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .form-select,
        .form-input {
          width: 100%;
          padding: 14px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
        }

        .form-select:focus,
        .form-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .modal-footer {
          padding: 24px 32px;
          border-top: 2px solid rgba(82,171,152,0.15);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn-cancel,
        .btn-submit {
          padding: 14px 28px;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-cancel {
          background: rgba(107,114,128,0.15);
          color: #374151;
        }

        .btn-cancel:hover {
          background: rgba(107,114,128,0.25);
        }

        .btn-submit {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 20px rgba(43,103,119,0.3);
        }

        .btn-submit:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(43,103,119,0.4);
        }

        .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #666;
        }

        .empty-state-icon {
          font-size: 80px;
          margin-bottom: 20px;
        }

        .empty-state-text {
          font-size: 18px;
          color: #2b6777;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .requests-grid {
            grid-template-columns: 1fr;
          }

          .controls-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            width: 100%;
          }

          .stats-row {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>

      {/* Stats Row */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Total</div>
          <div className="stat-value">{requests.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending</div>
          <div className="stat-value">
            {requests.filter(r => r.status === 'pending').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">In Progress</div>
          <div className="stat-value">
            {requests.filter(r => r.status === 'in-progress').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Completed</div>
          <div className="stat-value">
            {requests.filter(r => r.status === 'completed').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Filtered</div>
          <div className="stat-value">{filteredRequests.length}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by student, request number, room, or description..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={filterHostel}
          onChange={(e) => setFilterHostel(e.target.value)}
        >
          <option value="all">All Hostels</option>
          {hostels.map(hostel => (
            <option key={hostel.hostel_id} value={hostel.hostel_name}>
              {hostel.hostel_name}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="pending">⏳ Pending</option>
          <option value="in-progress">🔄 In Progress</option>
          <option value="completed">✅ Completed</option>
          <option value="cancelled">❌ Cancelled</option>
        </select>

        <select
          className="filter-select"
          value={filterIssueType}
          onChange={(e) => setFilterIssueType(e.target.value)}
        >
          <option value="all">All Issue Types</option>
          <option value="electrical">⚡ Electrical</option>
          <option value="plumbing">🚰 Plumbing</option>
          <option value="furniture">🪑 Furniture</option>
          <option value="carpentry">🔨 Carpentry</option>
          <option value="appliances">🔌 Appliances</option>
          <option value="internet">🌐 Internet</option>
          <option value="hvac">❄️ HVAC</option>
          <option value="other">🔧 Other</option>
        </select>
      </div>

      {/* Requests Grid */}
      {loading ? (
        <div className="empty-state">
          <div className="empty-state-icon">⏳</div>
          <div className="empty-state-text">Loading maintenance requests...</div>
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="empty-state-icon">❌</div>
          <div className="empty-state-text">{error}</div>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔧</div>
          <div className="empty-state-text">No maintenance requests found</div>
        </div>
      ) : (
        <div className="requests-grid">
          {filteredRequests.map((request) => (
            <div key={request.id} className="request-card">
              <div className="request-header">
                <div className="request-icon">
                  {getIssueTypeIcon(request.issue_type)}
                </div>
                <div className="request-info">
                  <div className="request-number">{request.request_number}</div>
                  <div className="request-student">{request.student_name}</div>
                  <div className="request-date">
                    {new Date(request.created_at).toLocaleDateString()} • {request.hostel_name}
                  </div>
                </div>
              </div>

              <div className="request-description">{request.description}</div>

              <div className="request-details">
                <div className="detail-item">
                  <span className="detail-label">Issue Type</span>
                  <span className="detail-value">{request.issue_type}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
                    {request.status}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Room</span>
                  <span className="detail-value">{request.room_number || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Assigned To</span>
                  <span className="detail-value">{request.assigned_to || 'Unassigned'}</span>
                </div>
              </div>

              <div className="request-actions">
                <button
                  className="action-btn-view"
                  onClick={() => handleViewDetails(request)}
                >
                  👁️ View
                </button>
                <button
                  className="action-btn-update"
                  onClick={() => handleOpenUpdate(request)}
                >
                  🔧 Update
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Maintenance Request Details</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-row">
                <div className="detail-row-label">Request Number</div>
                <div className="detail-row-value">{selectedRequest.request_number}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Student Name</div>
                <div className="detail-row-value">{selectedRequest.student_name}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Phone Number</div>
                <div className="detail-row-value">{selectedRequest.phone_number}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Email</div>
                <div className="detail-row-value">{selectedRequest.email}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Hostel</div>
                <div className="detail-row-value">{selectedRequest.hostel_name}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Room Number</div>
                <div className="detail-row-value">{selectedRequest.room_number || 'N/A'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Issue Type</div>
                <div className="detail-row-value">{selectedRequest.issue_type}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Status</div>
                <div className="detail-row-value">
                  <span className={`status-badge ${getStatusBadgeClass(selectedRequest.status)}`}>
                    {selectedRequest.status}
                  </span>
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Description</div>
                <div className="detail-row-value">{selectedRequest.description}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Assigned To</div>
                <div className="detail-row-value">{selectedRequest.assigned_to || 'Unassigned'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Submitted On</div>
                <div className="detail-row-value">
                  {new Date(selectedRequest.created_at).toLocaleString()}
                </div>
              </div>
              {selectedRequest.completed_at && (
                <div className="detail-row">
                  <div className="detail-row-label">Completed At</div>
                  <div className="detail-row-value">
                    {new Date(selectedRequest.completed_at).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Update Modal */}
      {showUpdateModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowUpdateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Update Maintenance Request</h2>
              <button className="modal-close" onClick={() => setShowUpdateModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateRequest}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Request Number</label>
                  <input
                    type="text"
                    className="form-input"
                    value={selectedRequest.request_number}
                    disabled
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Student</label>
                  <input
                    type="text"
                    className="form-input"
                    value={selectedRequest.student_name}
                    disabled
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select
                    className="form-select"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    required
                  >
                    <option value="pending">⏳ Pending</option>
                    <option value="in-progress">🔄 In Progress</option>
                    <option value="completed">✅ Completed</option>
                    <option value="cancelled">❌ Cancelled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Assign To</label>
                  <input
                    type="text"
                    className="form-input"
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                    placeholder="Enter staff name or technician..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowUpdateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  Update Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default ManageRequests;